import React from 'react';
import DhfFolderShell, { PhfTile } from './DhfFolderShell';
import {
  Sparkles, Search, GitBranch, FlaskConical, Target, Activity, Thermometer,
  Moon, Zap, CheckCircle2, AlertTriangle, LockKeyhole, Pill, RefreshCw,
  ArrowRight, CalendarClock, MessageSquareText
} from 'lucide-react';

/* Doctor Care Progress — Timeline tab of the DHF folder. Reuses .phf-* system. */

const Dot = ({ s }) => {
  const map = { ok:'#22c7a9', partial:'#f59e0b', miss:'#cc233e', up:'#d9e3ef' };
  return <span style={{ width: 9, height: 9, borderRadius: 99, background: map[s] }} />;
};

export default function DoctorCareProgressMock() {
  return (
    <DhfFolderShell
      activeTab="timeline"
      title={<>Care Progress <i>(Doctor cockpit — Cough)</i></>}
      subtitle="Recurrent pattern intelligence and cross-visit decision support for Dr. Chetna Tikoo."
      badges={[
        { icon: <CheckCircle2 size={16} />, label: 'Ongoing thread', tone: 'green' },
        { icon: <AlertTriangle size={16} />, label: 'Recurring pattern detected · 4 of last 5 visits', tone: 'blue' },
      ]}
    >
      <div className="phf-identity">
        <div className="phf-id-cell phf-id-cell--patient">
          <span className="phf-id-avatar">R</span>
          <div>
            <div className="phf-id-name">Rohan Sharma</div>
            <div className="phf-id-sub">34 M · +91 ••••3265</div>
          </div>
        </div>
        <div className="phf-id-cell"><span className="phf-id-ico"><CalendarClock size={18}/></span><div><div className="phf-id-label">Thread started</div><div className="phf-id-value">03 Jun 26</div></div></div>
        <div className="phf-id-cell"><span className="phf-id-ico"><CheckCircle2 size={18}/></span><div><div className="phf-id-label">Latest review</div><div className="phf-id-value">07 Jun 26</div></div></div>
        <div className="phf-id-cell"><span className="phf-id-ico"><MessageSquareText size={18}/></span><div><div className="phf-id-label">Adherence</div><div className="phf-id-value">4 of 5 days</div></div></div>
        <div className="phf-id-cell"><span className="phf-id-ico"><LockKeyhole size={18}/></span><div><div className="phf-id-label">Doctor-only insight</div><div className="phf-id-value">Enabled</div></div></div>
      </div>

      <div className="phf-grid">
        {/* Recurrent pattern insight */}
        <PhfTile icon={<Sparkles size={20} />} ico="phf-ico-green" title="Recurrent pattern insight"
          right={<span className="phf-chip phf-chip--green">4 / 5 episodes</span>}
          style={{ gridColumn: '1 / -1' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20 }}>
            <ul className="phf-checklist">
              <li><CheckCircle2 size={16} /><span>Cough has recurred in <b>4 of the last 5</b> health events.</span></li>
              <li><CheckCircle2 size={16} /><span>Seen across this doctor and shared external records.</span></li>
              <li><CheckCircle2 size={16} /><span>Pattern window: last <b>8 months</b>. Trigger cluster: winter / seasonal.</span></li>
              <li><CheckCircle2 size={16} /><span>Baseline risks flagged: smoking, DM (controlled).</span></li>
            </ul>
            <div>
              <div className="phf-id-label" style={{ marginBottom: 8 }}>Last 5 related events</div>
              <ul className="phf-checklist" style={{ gap: 8 }}>
                {[
                  { d:'03 Jun 26', l:'Cough',           tone:'green', tag:'Current' },
                  { d:'11 Apr 26', l:'Cough',           tone:'blue',  tag:'Past'    },
                  { d:'28 Feb 26', l:'Fever · no cough',tone:'blue',  tag:'Past'    },
                  { d:'17 Jan 26', l:'Cough',           tone:'blue',  tag:'Past'    },
                  { d:'08 Nov 25', l:'Cough',           tone:'blue',  tag:'Past'    },
                ].map((e,i) => (
                  <li key={i} style={{ justifyContent: 'space-between' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:10 }}>
                      <span style={{ fontSize: 12, color:'#526179', fontFamily:'Inter', width:70 }}>{e.d}</span>
                      <span style={{ color:'#07122f', fontWeight:600 }}>{e.l}</span>
                    </span>
                    <span className={`phf-chip ${e.tone === 'green' ? 'phf-chip--green' : 'phf-chip--blue'}`}>{e.tag}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #eef2f8', display: 'flex', gap: 8, flexWrap:'wrap' }}>
            <span className="phf-chip phf-chip--blue">Possible recurrent pattern</span>
            <span className="phf-chip phf-chip--blue">Needs root-cause review</span>
          </div>
        </PhfTile>

        {/* Quick actions */}
        <PhfTile icon={<Target size={20} />} ico="phf-ico-blue" title="Suggested next actions">
          <ul className="phf-checklist" style={{ gap: 10 }}>
            {[
              { l: 'Review recurrence detail',              Icon: Search      },
              { l: 'Convert to chronic / recurring thread', Icon: GitBranch   },
              { l: 'Order deeper work-up (chest X-ray, spirometry)', Icon: FlaskConical },
              { l: 'Check triggers · smoking cessation',    Icon: Target      },
            ].map((a,i) => (
              <li key={i} style={{ justifyContent: 'space-between', padding: '4px 0' }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:11 }}>
                  <a.Icon size={16} /><span>{a.l}</span>
                </span>
                <ArrowRight size={14} color="#526179" />
              </li>
            ))}
          </ul>
        </PhfTile>

        {/* Doctor-only insight */}
        <PhfTile icon={<LockKeyhole size={20} />} ico="phf-ico-blue" title="Doctor-only insight">
          <p style={{ color: '#526179' }}>
            Detailed recurrence intelligence, cross-visit patterning, and decision support are visible to you.
            Sensitive inferences remain doctor-facing unless clinically useful for the patient.
          </p>
          <p className="phf-med-note" style={{ marginTop: 8 }}>
            The patient view shows a simplified, non-alarming history and their milestones only.
          </p>
        </PhfTile>

        {/* 13-step cycle */}
        <PhfTile icon={<RefreshCw size={20} />} ico="phf-ico-blue" title="Clinical care cycle · this visit"
          right={<span className="phf-chip phf-chip--green">9 of 13 filled · 4 skipped intentionally</span>}
          style={{ gridColumn: '1 / -1' }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, overflowX:'auto', padding:'6px 2px' }}>
            {[
              [1,'Concern',true],[2,'Findings',true],[3,'Assessment',true],[4,'Working Dx',true],[5,'Tests',true],
              [6,'Reassess',false],[7,'Confirm Dx',true],[8,'Cause',true],[9,'Plan',true],
              [10,'Milestones',false],[11,'Feedback',true],[12,'Review',true],[13,'Close',false],
            ].map(([n,l,d], i, a) => (
              <React.Fragment key={n}>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', minWidth:66 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 99, display:'inline-flex', alignItems:'center', justifyContent:'center',
                    fontFamily:'Inter', fontWeight:800, fontSize:13,
                    background: d ? '#007f73' : '#fff',
                    color: d ? '#fff' : '#526179',
                    border: d ? 'none' : '1.5px dashed #d9e3ef',
                    boxShadow: d ? '0 6px 14px rgba(0,127,115,0.28)' : 'none',
                  }}>{n}</div>
                  <div style={{ fontSize: 10.5, marginTop: 6, color: d ? '#07122f' : '#526179', fontFamily:'Inter', fontWeight: 600 }}>{l}</div>
                </div>
                {i < a.length - 1 && <div style={{ height: 2, flex: 1, minWidth: 8, background: 'linear-gradient(90deg, rgba(0,127,115,0.14), rgba(0,127,115,0.22), rgba(0,127,115,0.14))', marginBottom: 20 }} />}
              </React.Fragment>
            ))}
          </div>
        </PhfTile>

        {/* Milestones with dots */}
        <PhfTile icon={<CheckCircle2 size={20} />} ico="phf-ico-green" title="Improvement milestones · tracked daily">
          <ul className="phf-checklist" style={{ gap: 14 }}>
            {[
              { l:'Cough freq should reduce', Icon:Activity,   hits:['ok','ok','ok','partial','miss','up','up'] },
              { l:'No fever',                 Icon:Thermometer, hits:['ok','ok','ok','ok','partial','up','up'] },
              { l:'Sleep better at night',    Icon:Moon,       hits:['ok','ok','ok','miss','partial','up','up'] },
              { l:'Energy improve',            Icon:Zap,        hits:['ok','ok','ok','ok','partial','up','up'] },
            ].map((m,i) => (
              <li key={i} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:11 }}>
                  <m.Icon size={16} /><span>{m.l}</span>
                </span>
                <span style={{ display:'inline-flex', gap:5 }}>
                  {m.hits.map((h,j) => <Dot key={j} s={h} />)}
                </span>
              </li>
            ))}
          </ul>
        </PhfTile>

        {/* Current medicines */}
        <PhfTile icon={<Pill size={20} />} ico="phf-ico-blue" title="Current medicines">
          <div className="phf-medwrap">
            <table className="phf-medtable">
              <colgroup>
                <col className="phf-c-med"/><col className="phf-c-str"/><col className="phf-c-dose"/><col className="phf-c-freq"/><col className="phf-c-dur"/><col className="phf-c-notes"/>
              </colgroup>
              <thead><tr><th>Medicine</th><th>Strength</th><th>Dose</th><th>Frequency</th><th>Duration</th><th>Targets milestone</th></tr></thead>
              <tbody>
                <tr><td>Paracetamol</td><td>500 mg</td><td>1 tab</td><td>Every 8 hours</td><td>5 days</td><td>No fever · Cough reduce</td></tr>
              </tbody>
            </table>
          </div>
        </PhfTile>

        {/* Care timeline */}
        <PhfTile icon={<CalendarClock size={20} />} ico="phf-ico-blue" title="Care timeline · trigger anchored">
          <ul className="phf-checklist" style={{ gap: 12 }}>
            <li><span style={{ width:22, height:22, borderRadius:99, background:'#e9f8f4', color:'#007f73', display:'inline-flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:11 }}>1</span><div><b>03 Jun 26</b> · Dr. Chetna Tikoo — paracetamol started 1-1-1</div></li>
            <li><span style={{ width:22, height:22, borderRadius:99, background:'#e9f8f4', color:'#007f73', display:'inline-flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:11 }}>2</span><div><b>04 Jun 26</b> · Patient — first update submitted (feeling better)</div></li>
            <li><span style={{ width:22, height:22, borderRadius:99, background:'#fff1f3', color:'#cc233e', display:'inline-flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:11 }}>3</span><div><b>05 Jun 26</b> · Patient — missed check-in</div></li>
            <li><span style={{ width:22, height:22, borderRadius:99, background:'#eef5ff', color:'#0b4f9f', display:'inline-flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:11 }}>4</span><div><b>07 Jun 26</b> · Dr. Chetna Tikoo — reviewed, trending improving</div></li>
          </ul>
        </PhfTile>

        {/* Daily check-in tracker */}
        <PhfTile icon={<CheckCircle2 size={20} />} ico="phf-ico-green" title="Patient daily check-in" style={{ gridColumn: '1 / -1' }}>
          <div style={{ display:'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 10 }}>
            {[
              {d:'04 Jun', s:'ok', l:'Submitted'},{d:'05 Jun', s:'miss', l:'Missed'},{d:'06 Jun', s:'ok', l:'Submitted'},{d:'07 Jun', s:'ok', l:'Submitted'},
              {d:'08 Jun', s:'partial', l:'Pending'},{d:'09 Jun', s:'up', l:'Upcoming'},{d:'10 Jun', s:'up', l:'Upcoming'},
            ].map((day,i) => (
              <div key={i} style={{
                padding: 12, textAlign: 'center', borderRadius: 12,
                border: '1px solid ' + ({ ok:'#c7ecdf', miss:'#ffd3da', partial:'#ffe3b2', up:'#d9e3ef' }[day.s]),
                background: ({ ok:'#eefaf5', miss:'#fff1f3', partial:'#fff7e6', up:'#fafbfd' }[day.s]),
              }}>
                <div style={{ fontSize: 11, color:'#526179', fontFamily:'Inter', fontWeight:600 }}>{day.d}</div>
                <div style={{ marginTop: 6 }}><Dot s={day.s === 'up' ? 'up' : day.s} /></div>
                <div style={{ fontSize: 11, color:'#07122f', marginTop: 6, fontWeight: 700 }}>{day.l}</div>
              </div>
            ))}
          </div>
        </PhfTile>
      </div>

      <div style={{ marginTop: 18, display:'flex', gap: 8, justifyContent:'flex-end' }}>
        <button className="phf-btn-secondary">Message patient</button>
        <button className="phf-btn-primary">Open DHF composer <ArrowRight size={16}/></button>
      </div>
    </DhfFolderShell>
  );
}
